import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from '../context/CartContext';
import { HiOutlineTrash, HiOutlinePlus, HiOutlineMinus, HiOutlineShoppingBag, HiArrowLeft } from 'react-icons/hi';
import toast from 'react-hot-toast';

const Cart = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cartItems, cartTotal, updateCartItem, removeFromCart, applyCoupon, removeCoupon, discount, coupon, loading } = useCart();
  const [couponCode, setCouponCode] = useState('');
  const [applyingCoupon, setApplyingCoupon] = useState(false);

  const handleQuantityChange = async (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    try {
      await updateCartItem(itemId, newQuantity);
    } catch (error) {
      toast.error(t('messages.error'));
    }
  };

  const handleRemoveItem = async (itemId) => {
    try {
      await removeFromCart(itemId);
      toast.success(t('messages.removedFromCart'));
    } catch (error) {
      toast.error(t('messages.error'));
    }
  };

  const handleApplyCoupon = async (e) => {
    e.preventDefault();
    if (!couponCode.trim()) return;

    setApplyingCoupon(true);
    try {
      await applyCoupon(couponCode);
      toast.success(t('messages.couponApplied'));
      setCouponCode('');
    } catch (error) {
      toast.error(error.response?.data?.message || t('messages.invalidCoupon'));
    } finally {
      setApplyingCoupon(false);
    }
  };

  const handleRemoveCoupon = async () => {
    try {
      await removeCoupon();
      toast.success(t('messages.couponRemoved'));
    } catch (error) {
      toast.error(t('messages.error'));
    }
  };

  const subtotal = cartTotal;
  const shipping = subtotal >= 450 ? 0 : 15;
  const total = subtotal - discount + shipping;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!cartItems || cartItems.length === 0) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center animate-fade-in">
        <div className="text-center">
          <HiOutlineShoppingBag className="w-20 h-20 mx-auto text-gray-300 mb-6" />
          <h2 className="font-serif text-3xl mb-4">{t('cart.emptyCart')}</h2>
          <p className="text-gray-500 mb-8">{t('cart.emptyCartDesc')}</p>
          <Link
            to="/shop"
            className="inline-block bg-luxury-charcoal text-white px-10 py-4 text-sm uppercase tracking-widest hover:bg-gray-800 transition-colors"
          >
            {t('cart.startShopping')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <section className="bg-primary-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-serif text-4xl md:text-5xl mb-4">{t('cart.shoppingCart')}</h1>
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
            <Link to="/" className="hover:text-luxury-gold transition-colors">Home</Link>
            <span>/</span>
            <span>Cart</span>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:flex lg:gap-12">
            {/* Cart Items */}
            <div className="flex-1">
              <div className="hidden md:grid md:grid-cols-12 gap-4 pb-4 border-b border-gray-100 text-sm uppercase tracking-wider text-gray-500">
                <div className="col-span-6">{t('cart.product')}</div>
                <div className="col-span-2 text-center">{t('cart.price')}</div>
                <div className="col-span-2 text-center">{t('cart.quantity')}</div>
                <div className="col-span-2 text-right">{t('cart.total')}</div>
              </div>

              <div className="divide-y divide-gray-100">
                {cartItems.map((item) => {
                  const product = item.product;
                  const variant = product?.variants?.find(v => v.sku === item.variant) || product?.variants?.[0];
                  const price = variant?.price || product?.price || 0;
                  const itemTotal = price * item.quantity;

                  return (
                    <div key={item._id} className="py-6 md:grid md:grid-cols-12 md:gap-4 md:items-center">
                      {/* Product */}
                      <div className="col-span-6 flex gap-4">
                        <Link to={`/product/${product?.slug}`} className="w-24 h-32 flex-shrink-0">
                          <img
                            src={product?.mainImage || product?.images?.[0]?.url || '/placeholder.jpg'}
                            alt={product?.name}
                            className="w-full h-full object-cover"
                          />
                        </Link>
                        <div className="flex-1">
                          <Link to={`/product/${product?.slug}`}>
                            <h3 className="font-serif text-lg hover:text-luxury-gold transition-colors">
                              {product?.name}
                            </h3>
                          </Link>
                          <p className="text-sm text-gray-500 mt-1">{variant?.name}</p>
                          <button
                            onClick={() => handleRemoveItem(item._id)}
                            className="text-sm text-gray-400 hover:text-red-500 transition-colors mt-2 flex items-center gap-1 md:hidden"
                          >
                            <HiOutlineTrash className="w-4 h-4" />
                            {t('common.remove')}
                          </button>
                        </div>
                      </div>

                      {/* Price */}
                      <div className="col-span-2 text-center hidden md:block">
                        {price.toFixed(2)} TND
                      </div>

                      {/* Quantity */}
                      <div className="col-span-2 flex justify-center mt-4 md:mt-0">
                        <div className="flex items-center border border-gray-200">
                          <button
                            onClick={() => handleQuantityChange(item._id, item.quantity - 1)}
                            className="p-2 hover:bg-gray-100 transition-colors"
                          >
                            <HiOutlineMinus className="w-4 h-4" />
                          </button>
                          <span className="w-10 text-center text-sm">{item.quantity}</span>
                          <button
                            onClick={() => handleQuantityChange(item._id, item.quantity + 1)}
                            disabled={item.quantity >= (variant?.stock || 10)}
                            className="p-2 hover:bg-gray-100 transition-colors disabled:opacity-50"
                          >
                            <HiOutlinePlus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Total & Remove */}
                      <div className="col-span-2 flex items-center justify-between md:justify-end gap-4 mt-4 md:mt-0">
                        <span className="font-medium md:hidden">{itemTotal.toFixed(2)} TND</span>
                        <span className="font-medium hidden md:block">{itemTotal.toFixed(2)} TND</span>
                        <button
                          onClick={() => handleRemoveItem(item._id)}
                          className="text-gray-400 hover:text-red-500 transition-colors hidden md:block"
                        >
                          <HiOutlineTrash className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Continue Shopping */}
              <div className="mt-8">
                <Link
                  to="/shop"
                  className="inline-flex items-center gap-2 text-sm uppercase tracking-widest hover:text-luxury-gold transition-colors"
                >
                  <HiArrowLeft className="w-4 h-4" />
                  {t('cart.continueShopping')}
                </Link>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:w-96 mt-12 lg:mt-0">
              <div className="bg-primary-50 p-6 lg:p-8 sticky top-24">
                <h2 className="font-serif text-xl mb-6">{t('cart.orderTotal')}</h2>

                {/* Coupon */}
                <div className="mb-6">
                  {coupon ? (
                    <div className="flex items-center justify-between p-3 bg-white border border-luxury-gold">
                      <div>
                        <span className="text-sm font-medium">{coupon.code}</span>
                        <span className="text-xs text-gray-500 ml-2">
                          ({coupon.type === 'percentage' ? `${coupon.value}% off` : `$${coupon.value} off`})
                        </span>
                      </div>
                      <button
                        onClick={handleRemoveCoupon}
                        className="text-gray-400 hover:text-red-500"
                      >
                        <HiOutlineTrash className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleApplyCoupon} className="flex gap-2">
                      <input
                        type="text"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        placeholder={t('cart.couponCode')}
                        className="flex-1 px-4 py-2 border border-gray-200 text-sm focus:outline-none focus:border-luxury-gold"
                      />
                      <button
                        type="submit"
                        disabled={applyingCoupon}
                        className="px-4 py-2 bg-luxury-charcoal text-white text-sm uppercase tracking-wider hover:bg-gray-800 transition-colors disabled:opacity-50"
                      >
                        {t('cart.applyCoupon')}
                      </button>
                    </form>
                  )}
                </div>

                {/* Summary Lines */}
                <div className="space-y-4 border-b border-gray-200 pb-6 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{t('cart.subtotal')}</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>{t('cart.discount')}</span>
                      <span>-${discount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{t('cart.shipping')}</span>
                    <span>{shipping === 0 ? t('cart.free') : `$${shipping.toFixed(2)}`}</span>
                  </div>
                  {shipping > 0 && (
                    <p className="text-xs text-gray-500">
                      {t('cart.addMoreForFreeShipping', { amount: (150 - subtotal).toFixed(2) })}
                    </p>
                  )}
                </div>

                {/* Total */}
                <div className="flex justify-between mb-6">
                  <span className="font-serif text-lg">{t('cart.total')}</span>
                  <span className="font-serif text-xl">${total.toFixed(2)}</span>
                </div>

                {/* Checkout Button */}
                <button
                  onClick={() => navigate('/checkout')}
                  className="w-full bg-luxury-charcoal text-white py-4 text-sm uppercase tracking-widest hover:bg-gray-800 transition-colors"
                >
                  {t('cart.proceedToCheckout')}
                </button>

                {/* Secure Checkout */}
                <p className="text-xs text-center text-gray-500 mt-4">
                  🔒 Secure checkout powered by Stripe
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Cart;
